import { EventEmitter, inject, Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom, map, Observable, tap } from 'rxjs';
import { productAccessConfig } from '@core/config/product-access.config';
import { FeatureToggleService, LocalStorageService, SecurityService } from '@mynexus/mynexus-ui-lib';
import { NotificationService } from '@core/services/notification.service';
import { LineOfBusinessReference, LineOfBusinessMap, ProductName, LineOfBusiness } from '@core/constants';
import { ExpandTableConfig, TableConfig } from '@shared/models/table-config.model';
// Rules
import pacConfig from '@products/pac/config/rules';
import hhConfig from '@products/hh/config/rules';
import dmeposConfig from '@products/dmepos/config/rules';
import configMappings from '@products/mappings';
import { ProductConfiguration } from '@shared/models/product-options.model';
import { HttpClient } from '@angular/common/http';
import { API_PLATFORM_URL } from '@core/config';
import { keyBy } from 'lodash-es';

@Injectable({
  providedIn: 'root',
})
export class PlatformService {
  private readonly apiUrl = inject(API_PLATFORM_URL);
  private readonly http = inject(HttpClient);
  private readonly securityService = inject(SecurityService);
  private readonly notificationService = inject(NotificationService);
  private readonly featureToggleService = inject(FeatureToggleService);
  private readonly localStorage = inject(LocalStorageService);

  hasUnsavedChanges = false;

  lineOfBusinessMap$ = new BehaviorSubject<LineOfBusinessMap | null>(null);

  readonly currentProduct = new BehaviorSubject<ProductName | null>(null);
  readonly currentLineOfBusiness$ = new BehaviorSubject<LineOfBusinessReference | null>(null);

  /**
   * @deprecated Check if this can be removed
   */
  readonly currentProductChange$ = new EventEmitter();

  private productsConfig!: Record<ProductName, ProductConfiguration>;

  getLineOfBusinesses(): Promise<LineOfBusinessMap | null> {
    if (this.lineOfBusinessMap$.value) {
      return lastValueFrom(this.lineOfBusinessMap$);
    }

    return lastValueFrom(
      this.http.get<LineOfBusinessReference[]>(`${this.apiUrl}/lineOfBusiness`).pipe(
        tap((res) => this.lineOfBusinessMap$.next(keyBy(res, 'applicationKey') as LineOfBusinessMap)),
        map(() => this.lineOfBusinessMap$.value)
      )
    );
  }

  getPermissions(): Observable<Record<string, string[]>> {
    return this.http.post<Record<string, string[]>>(
      `${this.apiUrl}/Permissions`,
      null
    );
  }

  getProductsAccessConfig() {
    return productAccessConfig.reduce(async (acc, product) => {
      let configArr = await acc;

      if (!product.featureToggle) {
        configArr.push(product);
      } else {
        const featureEnabled = await lastValueFrom(this.featureToggleService.getFeatureAccess(product.featureToggle));

        if (featureEnabled) {
          configArr.push(product);
        }
      }

      return acc;
    }, Promise.resolve([] as ProductConfiguration[]));
  }

  async getDefaultProduct() {
    const config = await this.getProductsAccessConfig();

    this.productsConfig = keyBy(config, 'option.value') as Record<ProductName, ProductConfiguration>;

    const savedProduct = this.localStorage.getItem('product');

    const defaultProduct = config.find(
      (p) => (p?.option.value === savedProduct || !savedProduct) && this.securityService.hasAccess(p.access)
    );

    if (!defaultProduct && savedProduct) {
      this.localStorage.removeItem('product');
    }

    const defaultProductName = defaultProduct?.option.value || null;

    if (defaultProductName) {
      this.setCurrentProduct(defaultProductName);
    }

    return defaultProductName;
  }

  setCurrentProduct(product: ProductName) {
    this.currentProduct.next(product);
    this.currentProductChange$.next(product);

    this.localStorage.setItem('product', product);

    const selectedLOB = this.productsConfig[product].option.lineOfBusiness as LineOfBusiness;

    if (this.lineOfBusinessMap$.value) {
      this.currentLineOfBusiness$.next(this.lineOfBusinessMap$.value[selectedLOB]);
    }
  }

  confirmUnsavedChangesNavigation(): Promise<any> {
    return this.notificationService.confirm({
      header: 'Your changes will be lost',
      message: 'You have unsaved changes that will be lost. Are you sure you want to continue?',
      acceptLabel: 'Discard Changes',
      acceptButtonStyleClass: 'p-button-danger',
      rejectLabel: 'Back',
      defaultFocus: 'reject',
    });
  }

  getRulesConfig(): TableConfig {
    switch (this.currentProduct.value) {
      case ProductName.PAC_MA:
      case ProductName.PAC_COM:
        return pacConfig[this.currentProduct.value];
      case ProductName.HH:
        return hhConfig;
      case ProductName.DMEPOS_MA:
      case ProductName.DMEPOS_MCD:
        return dmeposConfig[this.currentProduct.value];
      default:
        throw new Error(`Table configuration for ${this.currentProduct.value} is not defined`);
    }
  }

  getMetricsConfig(): Promise<ExpandTableConfig> {
    return this.loadConfig(configMappings.metrics[this.currentProduct.value!]);
  }

  getQueueConfig(): Promise<ExpandTableConfig> {
    return this.loadConfig(configMappings.queue[this.currentProduct.value!]);
  }

  private async loadConfig(resolver: any) {
    if (!resolver) {
      throw new Error(`Table configuration for ${this.currentProduct.value} is not defined`);
    }

    const config = await resolver();

    return config.default;
  }
}
